import React, { useState, useEffect } from 'react'
import { Box, Button, TextField } from '@mui/material'
import IntelLayout from './components/IntelLayout'
import KpiCard from './components/KpiCard'
import Badge from './components/Badge'
import IntelTable from './components/IntelTable'
import { intelColors, formatNumber } from './components/intelTheme'
import { intelApi } from './api'

const inr = (v) => '₹' + formatNumber(Math.round(v || 0))
const pct = (v) => (v == null ? '—' : `${v}%`)
const unwrap = (res) => res?.data?.responseObj || res?.data?.data || res?.data

const fmtDate = (d) => {
  if (!d) return '—'
  const dt = new Date(d)
  return isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const coverageBadge = (s) => {
  if (s === 'Full') return <Badge color='green'>Full</Badge>
  if (s === 'Partial') return <Badge color='orange'>Partial</Badge>
  return <Badge color='red'>None</Badge>
}
const statusBadge = (s) => {
  if (s === 'Healthy') return <Badge color='green'>✓ Healthy</Badge>
  if (s === 'Watch') return <Badge color='orange'>⚠ Watch</Badge>
  return <Badge color='red'>⚠ Risk</Badge>
}

const Field = ({ label, value }) => (
  <Box>
    <Box sx={{ fontSize: 10.5, fontWeight: 700, color: intelColors.muted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</Box>
    <Box sx={{ fontSize: 14, fontWeight: 600, color: intelColors.text, mt: '2px' }}>{value}</Box>
  </Box>
)
const SectionTitle = ({ children }) => (
  <Box sx={{ fontSize: 13, fontWeight: 700, color: intelColors.text, m: '10px 0 10px' }}>{children}</Box>
)

export default function CoverageAnalysis() {
  const [pin, setPin] = useState('')
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  const [overview, setOverview] = useState(null) // default view: KPIs + top pincodes (Pace + Mongo)
  const [data, setData] = useState(null) // serviceability profile (Mongo)
  const [ord, setOrd] = useState(null) // order specialist metrics (Pace)
  const [history, setHistory] = useState(null) // coverage history (Mongo snapshot log)
  const [searched, setSearched] = useState(false)

  // Default landing view — coverage KPIs + top pincodes.
  useEffect(() => {
    intelApi.coverageOverview().then((r) => setOverview(unwrap(r))).catch(() => {})
  }, [])

  const run = () => {
    const p = (pin || '').trim()
    if (!/^\d{6}$/.test(p)) {
      setErr('Enter a valid 6-digit pincode')
      return
    }
    setLoading(true)
    setErr(null)
    setData(null)
    setOrd(null)
    setHistory(null)
    setSearched(true)
    // Profile (Mongo) + order metrics (Pace) + coverage history (Mongo) — independent reads, never merged.
    Promise.allSettled([
      intelApi.coverageAnalysis({ pincode: p }).then((r) => setData(unwrap(r))),
      intelApi.coverageOrderStats({ pincode: p }).then((r) => setOrd(unwrap(r))),
      intelApi.coverageHistory({ pincode: p }).then((r) => setHistory(unwrap(r))),
    ])
      .then((rs) => {
        if (rs[0].status === 'rejected') setErr(rs[0].reason?.response?.data?.exception || rs[0].reason?.message || 'Lookup failed')
      })
      .finally(() => setLoading(false))
  }

  const reset = () => {
    setPin('')
    setSearched(false)
    setData(null)
    setOrd(null)
    setHistory(null)
    setErr(null)
  }

  const profile = data?.profile
  const couriers = ord?.couriers || []
  const kpis = overview?.kpis
  const topPins = overview?.topPincodes || []

  // Service Modes — derived from the profile's serving couriers (delivery → Forward, pickup → Reverse).
  const serviceModes = (() => {
    if (!profile) return []
    const acc = {}
    const add = (list, direction) =>
      (list || []).forEach((c) => {
        const st = c.serviceType || '—'
        const key = `${st}|${direction}`
        if (!acc[key]) acc[key] = { serviceType: st, direction, couriers: 0 }
        acc[key].couriers += 1
      })
    add(profile.deliveryCouriers, 'Forward')
    add(profile.pickupCouriers, 'Reverse')
    return Object.values(acc).sort((a, b) => b.couriers - a.couriers)
  })()

  const topCols = [
    { key: 'pincode', header: 'Pincode', render: (r) => <strong>{r.pincode}</strong> },
    { key: 'city', header: 'City', render: (r) => r.city || '—' },
    { key: 'state', header: 'State', render: (r) => r.state || '—' },
    { key: 'zone', header: 'Zone', render: (r) => r.zone || '—' },
    { key: 'orders', header: 'Orders (30d)', render: (r) => formatNumber(r.orders) },
    { key: 'gmv', header: 'GMV (30d)', render: (r) => inr(r.gmv) },
    { key: 'couriersActive', header: 'Couriers Active', render: (r) => formatNumber(r.couriersActive) },
    { key: 'deliveryCoverage', header: 'Delivery Coverage', render: (r) => coverageBadge(r.deliveryCoverage) },
    { key: 'pickupCoverage', header: 'Pickup Coverage', render: (r) => coverageBadge(r.pickupCoverage) },
    { key: 'cod', header: 'COD', render: (r) => (r.cod ? <Badge color='green'>Yes</Badge> : <Badge color='grey'>No</Badge>) },
    { key: 'sddNdd', header: 'SDD/NDD', render: (r) => (r.sddNdd && r.sddNdd !== 'None' ? <Badge color='purple'>{r.sddNdd}</Badge> : '—') },
    { key: 'status', header: 'Status', render: (r) => statusBadge(r.status) },
  ]
  const delCols = [
    { key: 'code', header: 'Courier Code', render: (r) => <strong>{r.code}</strong> },
    { key: 'parent', header: 'Parent', render: (r) => r.parent || '—' },
    { key: 'serviceType', header: 'Service Type', render: (r) => (r.serviceType ? <Badge color='blue'>{r.serviceType}</Badge> : '—') },
    { key: 'cod', header: 'COD?', render: (r) => (r.cod ? <Badge color='green'>Yes</Badge> : <Badge color='grey'>No</Badge>) },
    { key: 'maxWeightKg', header: 'Max Weight', render: (r) => (r.maxWeightKg != null ? `${r.maxWeightKg} kg` : '—') },
    { key: 'sla', header: 'Est. Transit', render: (r) => r.sla || '—' },
  ]
  const pickupCols = [
    { key: 'code', header: 'Courier Code', render: (r) => <strong>{r.code}</strong> },
    { key: 'parent', header: 'Parent', render: (r) => r.parent || '—' },
    { key: 'serviceType', header: 'Service Type', render: (r) => (r.serviceType ? <Badge color='orange'>{r.serviceType}</Badge> : '—') },
  ]
  const modeCols = [
    { key: 'serviceType', header: 'Service Type', render: (r) => <strong>{r.serviceType}</strong> },
    { key: 'direction', header: 'Direction', render: (r) => <Badge color={r.direction === 'Forward' ? 'blue' : 'orange'}>{r.direction}</Badge> },
    { key: 'couriers', header: '# Couriers', render: (r) => formatNumber(r.couriers) },
    { key: 'status', header: 'Coverage Status', render: () => <Badge color='green'>Covered</Badge> },
  ]
  const histCols = [
    { key: 'date', header: 'Date', render: (r) => fmtDate(r.date) },
    { key: 'event', header: 'Event', render: (r) => <Badge color='blue'>{(r.event || '').replace(/_/g, ' ')}</Badge> },
    { key: 'courierCode', header: 'Courier Code', render: (r) => r.courierCode || '—' },
    { key: 'serviceType', header: 'Service Type', render: (r) => r.serviceType || '—' },
    { key: 'pincodeType', header: 'List', render: (r) => r.pincodeType || '—' },
    { key: 'changedBy', header: 'Changed By', render: (r) => r.changedBy || '—' },
  ]

  const courierAvail = ord && profile ? `${formatNumber(couriers.length)} / ${formatNumber(profile.deliveryCourierCount)}` : '—'
  const pickupFailPct =
    ord && ord.pickupAttempted > 0 ? `${Math.round((ord.pickupFailed / ord.pickupAttempted) * 1000) / 10}% failure rate` : 'No pickups'

  return (
    <IntelLayout title='Pincode Coverage — PIN Code Analysis'>
      <Box sx={{ background: '#fff', border: `1px solid ${intelColors.border}`, borderRadius: '8px', p: '14px 18px', mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField label='Pincode (6 digits)' value={pin} onChange={(e) => setPin(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && run()} size='small' inputProps={{ maxLength: 6 }} sx={{ minWidth: 200 }} />
        <Button variant='contained' onClick={run}>Search Pincode</Button>
        {searched && <Button variant='outlined' color='error' onClick={reset}>Reset</Button>}
        <Box sx={{ fontSize: 11, color: intelColors.muted }}>Enter a 6-digit pincode for its full serviceability profile. Leave blank for the coverage overview.</Box>
      </Box>

      {err && (
        <Box sx={{ background: '#ffebee', border: '1px solid #ef9a9a', color: '#c62828', borderRadius: '6px', p: '10px 14px', mb: '14px', fontSize: 12 }}>{err}</Box>
      )}

      {!searched ? (
        <>
          {/* ── Default: coverage KPIs (L1 classification) ── */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', mb: 2 }}>
            <KpiCard loading={loading} color='blue' label='Total Active Pincodes' value={kpis ? formatNumber(kpis.totalActivePincodes) : '—'} sub='Serviceability universe' />
            <KpiCard loading={loading} color='green' label='Fully Serviceable' value={kpis ? formatNumber(kpis.fullyServiceable) : '—'} sub='≥3 delivery couriers' />
            <KpiCard loading={loading} color='orange' label='Partial Coverage' value={kpis ? formatNumber(kpis.partialCoverage) : '—'} sub='1–2 couriers' />
            <KpiCard loading={loading} color='red' label='Zero Coverage' value={kpis ? formatNumber(kpis.zeroCoverage) : '—'} sub='No courier reachable' />
          </Box>
          <IntelTable
            title='Top Pincodes by Order Volume — Last 30 Days'
            loading={!overview}
            columns={topCols}
            rows={topPins}
            rowKey={(r, i) => r.pincode || i}
            pageSize={25}
            emptyMsg='No order volume available (Pace analytics not configured).'
          />
        </>
      ) : (
        <>
          {/* ── Pincode Summary ── */}
          {profile && (
            <Box sx={{ background: '#fff', border: `1px solid ${intelColors.border}`, borderRadius: '8px', p: '16px 18px', mb: 2 }}>
              <Box sx={{ fontSize: 13, fontWeight: 700, color: intelColors.text, mb: 1.5 }}>📍 Pincode Summary — {data.pincode}</Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 2 }}>
                <Field label='Pincode' value={data.pincode} />
                <Field label='City' value={profile.city || '—'} />
                <Field label='State' value={profile.state || '—'} />
                <Field label='Zone' value={profile.region ? <Badge color='purple'>{profile.region}</Badge> : '—'} />
                <Field label='Delivery Couriers' value={formatNumber(profile.deliveryCourierCount)} />
                <Field label='Pickup Couriers' value={formatNumber(profile.pickupCourierCount)} />
                <Field label='COD Available?' value={profile.codAvailable ? <Badge color='green'>Yes</Badge> : <Badge color='grey'>No</Badge>} />
                <Field label='SDD / NDD?' value={profile.sddAvailable || profile.nddAvailable ? (<>{profile.sddAvailable ? <Badge color='purple'>SDD</Badge> : null} {profile.nddAvailable ? <Badge color='purple'>NDD</Badge> : null}</>) : <Badge color='grey'>None</Badge>} />
              </Box>
            </Box>
          )}

          {/* ── Specialist Metrics (Pace) ── */}
          <SectionTitle>📊 Specialist Metrics — Last 30 Days</SectionTitle>
          {ord ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '14px', mb: 2 }}>
              <KpiCard loading={loading} color='blue' label='First-Mile Pickup Attempts' value={formatNumber(ord.pickupAttempted)} sub='Reverse pickups scheduled' />
              <KpiCard loading={loading} color='red' label='Pickup Failures' value={formatNumber(ord.pickupFailed)} sub={pickupFailPct} />
              <KpiCard loading={loading} color='green' label='Last-Mile Delivery Rate' value={pct(ord.deliveryPct)} sub={`${formatNumber(ord.delivered)} delivered`} />
              <KpiCard loading={loading} color='orange' label='RTO Rate' value={pct(ord.rtoPct)} sub={`${formatNumber(ord.rto)} RTO'd`} />
              <KpiCard loading={loading} color='blue' label='Courier Availability' value={courierAvail} sub='Active / serviceable' />
            </Box>
          ) : (
            <Box sx={{ fontSize: 12, color: intelColors.muted, p: 2, border: `1px dashed ${intelColors.border}`, borderRadius: '6px', mb: 2 }}>
              Order metrics unavailable (Pace analytics not configured).
            </Box>
          )}

          {/* ── Delivery Couriers ── */}
          <IntelTable
            title='🚚 Delivery Couriers for this Pincode'
            loading={loading}
            columns={delCols}
            rows={profile?.deliveryCouriers || []}
            rowKey={(r, i) => r.code || i}
            pageSize={25}
            emptyMsg='No L1 delivery couriers for this pincode (L1 may not be seeded for it).'
          />

          {/* ── Pickup Couriers ── */}
          <IntelTable
            title='📦 Pickup Couriers for this Pincode'
            loading={loading}
            columns={pickupCols}
            rows={profile?.pickupCouriers || []}
            rowKey={(r, i) => r.code || i}
            pageSize={25}
            emptyMsg='No L1 pickup couriers for this pincode.'
          />

          {/* ── Service Modes Available ── */}
          <IntelTable
            title='🏷️ Service Modes Available at this Pincode'
            loading={loading}
            columns={modeCols}
            rows={serviceModes}
            rowKey={(r, i) => `${r.serviceType}-${r.direction}-${i}`}
            pageSize={25}
            emptyMsg='No service modes available.'
          />

          {/* ── Coverage History ── */}
          <IntelTable
            title={`📅 Coverage History — Pincode ${data?.pincode || ''}`}
            loading={loading}
            columns={histCols}
            rows={history?.events || []}
            rowKey={(r, i) => i}
            pageSize={25}
            emptyMsg='No coverage-change events recorded for this pincode.'
          />
        </>
      )}
    </IntelLayout>
  )
}
